import { useState, useCallback, useMemo } from 'react';
import LessonIssueApiUtils from '@utils/api/lessonIssue';
import { useFlashMessage } from '@root/hooks/use-flash-message';
import type {
  ResolveIssueModalContainerProps,
  ResolveIssueOption,
} from './resolve-issue-modal.types';

const FIFTY_MINUTES = 3000000;

export const useResolveIssueModalService = (
  props: ResolveIssueModalContainerProps,
) => {
  const { hideModal, addModalMessage, clearModalMessage, lesson } = props;

  const { addFlash } = useFlashMessage();

  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const actionOptions: ResolveIssueOption[] = useMemo(() => {
    if (lesson.end_at - lesson.start_at === FIFTY_MINUTES) {
      return [
        { value: 'Mark lesson as completed and issue 100% refund', id: '0' },
        { value: 'Mark lesson as completed and issue 50% rerund', id: '1' },
        { value: 'Mark lesson as completed', id: '2' },
      ];
    }
    return [
      { value: 'Mark lesson as completed and issue 100% refund', id: '0' },
      { value: 'Mark lesson as completed', id: '2' },
    ];
  }, [lesson.end_at, lesson.start_at]);

  const reasonOptions: ResolveIssueOption[] = useMemo(
    () => [
      { value: 'Lesson was delivered', id: '0' },
      { value: 'Unstable internet connection', id: '1' },
      { value: 'Blackout', id: '2' },
      { value: 'Computer problem', id: '3' },
      { value: 'Sickness', id: '4' },
      { value: 'Taking care of sick family member', id: '5' },
      { value: 'Other tutor-side reason', id: '6' },
      { value: 'Lesson was delivered', id: '7' },
      { value: 'Student related reason', id: '8' },
      { value: 'LingualBox related reason', id: '9' },
    ],
    [],
  );

  const handleActionChange = useCallback((value: string) => {
    setSelectedAction(value);
  }, []);

  const handleReasonChange = useCallback((value: string) => {
    setSelectedReason(value);
  }, []);

  const handleCommentChange = useCallback((value: string) => {
    setComment(value);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent | React.MouseEvent) => {
      e.preventDefault();

      if (selectedAction === null || selectedReason === null || comment === '') {
        addModalMessage('Action, Reason, and comment cannot be empty.', {
          autoDisappear: false,
          type: 'error',
        });
        return;
      }

      clearModalMessage();
      setIsSubmitting(true);

      try {
        await LessonIssueApiUtils.resolve(
          lesson.id,
          selectedAction,
          selectedReason,
          comment,
        );

        addFlash('Successfully resolved the issue', {
          autoDisappear: true,
          sticky: true,
          type: 'success',
        });
        hideModal();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'An error occurred';
        addModalMessage(errorMessage, {
          autoDisappear: false,
          type: 'error',
        });
        setIsSubmitting(false);
      }
    },
    [
      selectedAction,
      selectedReason,
      comment,
      lesson.id,
      addModalMessage,
      clearModalMessage,
      addFlash,
      hideModal,
    ],
  );

  const handleCancel = useCallback(() => {
    hideModal();
  }, [hideModal]);

  const issue = lesson.lesson_issue;

  return {
    selectedAction,
    selectedReason,
    comment,
    actionOptions,
    reasonOptions,
    isSubmitting,
    issueType: issue.issue_types[0] || 'Loading',
    issueComment: issue.comment,
    message: props.message,
    messageType: props.messageType,
    onActionChange: handleActionChange,
    onReasonChange: handleReasonChange,
    onCommentChange: handleCommentChange,
    onSubmit: handleSubmit,
    onCancel: handleCancel,
  };
};
